import {
  Col,
  Typography
} from 'antd';
import React from 'react';

import AdminUsersDropdown from 'components/forms/AdminUsersDropdown';
import FormItemEmail from 'components/forms/FormItemEmail';
import Enterprise, { EnterpriseStatus } from 'types/models/enterprise';

import { usePendingOwnerNotice } from './usePendingOwnerNotice';

const isDisabledOrDuplicate = (status: EnterpriseStatus) => {
  return status === EnterpriseStatus.DISABLED || status === EnterpriseStatus.DUPLICATE;
};

type Props = {
  enterpriseId: Enterprise['id'] | undefined
  initialOwner: Enterprise['owner']
  status: Enterprise['status']
  readOnly?: boolean
  isBackOffice?: boolean,
  isFullSpanOnMobileHalfOnDesktop?: boolean
};

export const EditEnterpriseOwner = ({
  enterpriseId,
  initialOwner,
  status,
  readOnly,
  isBackOffice = false,
  isFullSpanOnMobileHalfOnDesktop
}: Props) => {
  const isEnterpriseDisabledOrDuplicate = isDisabledOrDuplicate(status);
  const pendingOwnerNotice = usePendingOwnerNotice(
    enterpriseId,
    initialOwner?.id,
    isEnterpriseDisabledOrDuplicate
  );

  const renderMessage = () => {
    let message: JSX.Element | undefined = undefined;
    if (isEnterpriseDisabledOrDuplicate) {
      message = <Typography.Paragraph>Cannot invite/select owner for a {status} enterprise</Typography.Paragraph>;
    } else if (pendingOwnerNotice) {
      message = <Typography.Paragraph>{pendingOwnerNotice}</Typography.Paragraph>;
    }

    return message
      ? <Col span={24}>{message}</Col>
      : <></>;
  };

  const renderField = () => {
    /**
     * Hide owner invite / selection when:
     *   Enterprise status is Disabled or Duplicate OR
     *   Enterprise is pending owner (no meaningful owner information to show) and when the form is read-only
     */
    if (
      isEnterpriseDisabledOrDuplicate ||
      (pendingOwnerNotice && readOnly)
    ) {
      return <></>;
    }

    return (
      <AdminUsersDropdown
        enterpriseId={enterpriseId}
        readOnly={readOnly}
        adminTitle="Enterprise Owner"
        initialOwner={initialOwner}
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        emptyUsersComponent={
          // Email field should only be shown in back office
          isBackOffice
            ? (
              <Col span={24}>
                <FormItemEmail name="ownerEmail" readOnly={readOnly} />
              </Col>
            )
            : undefined
        }
      />
    );
  };

  return (
    <>
      {isBackOffice && renderMessage()}
      {renderField()}
    </>
  );
};